/**
 * ModeContext — Rehearsal vs Studio vs Notes.
 *
 * Reads the current `react-router` location and exposes a single literal
 * for chrome conditionals. Layouts swap on this; primitives never branch
 * on it directly.
 *
 * Reference: docs/console-architecture.md §5.1 ("a tiny `ModeContext`
 * that wraps `useLocation()`").
 */

import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

export type ConsoleMode = "rehearsal" | "studio" | "notes" | "auth" | "marketing";

export interface ModeContextValue {
  readonly mode: ConsoleMode;
}

const ModeContext = createContext<ModeContextValue>({ mode: "marketing" });

export interface ModeProviderProps {
  readonly children: ReactNode;
}

function deriveMode(pathname: string): ConsoleMode {
  if (pathname === "/") return "marketing";
  if (pathname.startsWith("/rehearsal")) return "rehearsal";
  if (pathname.startsWith("/studio")) return "studio";
  if (pathname.startsWith("/notes") || pathname.startsWith("/docs")) return "notes";
  if (pathname.startsWith("/auth")) return "auth";
  return "marketing";
}

export function ModeProvider({ children }: ModeProviderProps) {
  const location = useLocation();
  const value = useMemo<ModeContextValue>(
    () => ({ mode: deriveMode(location.pathname) }),
    [location.pathname],
  );
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): ModeContextValue {
  return useContext(ModeContext);
}
